import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useLayoutEffect, useRef, useState } from "react";

// Must set worker in the same module as Document/Page (react-pdf requirement).
// Vite: ?url resolves the worker from node_modules and gives a stable build path.
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

interface PdfViewerProps {
	file: string | Blob | ArrayBuffer;
	className?: string;
}

function measureContainerWidth(el: HTMLDivElement): number {
	const width = el.getBoundingClientRect().width;
	if (width > 0) return width;

	const parentWidth = el.parentElement?.getBoundingClientRect().width ?? 0;
	if (parentWidth > 0) return parentWidth;

	return Math.max(window.innerWidth - 48, 320);
}

export function PdfViewer({ file, className }: PdfViewerProps) {
	const [numPages, setNumPages] = useState<number | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const updateWidth = () => {
			setContainerWidth(measureContainerWidth(el));
		};

		updateWidth();
		const ro = new ResizeObserver(updateWidth);
		ro.observe(el);
		if (el.parentElement) ro.observe(el.parentElement);

		return () => ro.disconnect();
	}, [file]);

	const pageWidth = containerWidth ?? undefined;

	return (
		<div
			ref={containerRef}
			className={`flex h-full min-h-0 max-w-full flex-1 flex-col overflow-auto ${className ?? ""}`}
		>
			<Document
				file={file}
				onLoadSuccess={({ numPages: n }) => {
					setNumPages(n);
					setLoadError(null);
				}}
				onLoadError={(error) => {
					setLoadError(error?.message ?? "Failed to load PDF");
				}}
				loading={
					<div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
						Loading…
					</div>
				}
				error={
					loadError ? (
						<div className="py-4 text-sm text-destructive">{loadError}</div>
					) : null
				}
			>
				{numPages != null &&
					pageWidth != null &&
					Array.from({ length: numPages }, (_, i) => (
						<Page
							key={`page-${i + 1}`}
							pageNumber={i + 1}
							width={pageWidth}
							className="!mb-4 last:!mb-0"
							renderTextLayer
							renderAnnotationLayer
						/>
					))}
			</Document>
		</div>
	);
}
